import type { FC } from "react";
import React, { useEffect } from "react";
import { Link } from "react-router";
import type { Breadcrumb as BreadcrumbItem } from "@/types/view";
import {
  breadCrumbSelector,
  currentRouteSelector,
  removeBreadCrumbs,
} from "@/features/currentRouteSlice";
import { getUrlFromPath } from "@/routerConfig";
import { useAppDispatch, useAppSelector } from "@/storeHooks";
import css from "./AppHeader.module.css";
import { isNavigableBreadcrumbPath } from "./breadcrumb.helpers";

const renderCrumbLink = (crumb: BreadcrumbItem, path: string) =>
  crumb.isInternal ? (
    <Link to={path}>{crumb.label}</Link>
  ) : (
    <a href={getUrlFromPath(path)}>{crumb.label}</a>
  );

const BREADCRUMB_ALLOWED_PATHS = ["/dashboards"];

const Breadcrumb: FC<{ testId?: string }> = ({ testId = "breadcrumb" }) => {
  const dispatch = useAppDispatch();
  const breadcrumb = useAppSelector(breadCrumbSelector);
  const currentRoute = useAppSelector(currentRouteSelector);

  // Clear breadcrumbs when navigating to a disallowed path
  useEffect(() => {
    const isAllowedPath = BREADCRUMB_ALLOWED_PATHS.some((path) =>
      currentRoute.startsWith(path)
    );
    if (!isAllowedPath && breadcrumb.length > 0) {
      dispatch(removeBreadCrumbs());
    }
  }, [currentRoute, dispatch, breadcrumb]);

  const shouldShowBreadcrumbs =
    breadcrumb.length > 0 &&
    BREADCRUMB_ALLOWED_PATHS.some((path) => currentRoute.startsWith(path));

  if (!shouldShowBreadcrumbs) {
    return null;
  }

  return (
    <nav
      className={css.breadCrumb}
      aria-label="breadcrumbs"
      data-testid={testId}
    >
      <ol>
        {breadcrumb.map((crumb, index) => {
          if (index === breadcrumb.length - 1) {
            return (
              <li key={crumb.label} className={css.breadCrumbText}>
                {crumb.label}
              </li>
            );
          }

          const path = crumb.path?.split("?")?.[0] ?? "";
          const linkable = isNavigableBreadcrumbPath(crumb.path);

          return (
            <li key={crumb.label} className={css.breadCrumbText}>
              {linkable ? renderCrumbLink(crumb, path) : crumb.label}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
